import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@app/shared/services/route-guards/auth.guard';
import { ProgramType } from '../shared/enums';
import { BlankComponent } from './common-features/blank-component/blank.component';
import { ResponsiveGuard } from '@app/shared/services/route-guards/responsive.guard';
import { RoleGuard } from 'src/app/shared/services/route-guards/role.guard';
import { ProgramRole } from 'src/app/shared/enums/program-role.enum';
import { SiteRole } from 'src/app/shared/enums/site-role.enum';
import { TargetSiteProgramGuard } from 'src/app/shared/services/route-guards/target-site-program.guard';
import { RedirectGuard } from 'src/app/shared/services/route-guards/redirect.guard';
import { EnableOnSiteGuard } from '@app/shared/services/route-guards/enable-on-site.guard';
import { EidCallbackComponent } from '@app/auth/eid-callback/eid-callback.component';
import { EidLoggedInComponent } from '@app/auth/eid-logged-in/eid-logged-in.component';
import { EidAppswitchComponent } from '@app/auth/eid-appswitch/eid-appswitch.component';
import { SignedOutComponent } from '@app/auth/signed-out/signed-out.component';
import { RedirectEidGuard } from '@shared/services/route-guards/redirect-eid.guard';

const rootRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [RedirectGuard],
    component: BlankComponent
  },
  {
    path: 'blank',
    component: BlankComponent,
    canActivateChild: [AuthGuard],
    data: { stateName: 'Blank' }
  },
  {
    path: 'eid-callback',
    component: EidCallbackComponent,
    data: { standalone: true }
  },
  {
    path: 'eid-loggedin',
    component: EidLoggedInComponent,
    data: { standalone: true }
  },
  {
    path: 'eid-appswitch',
    component: EidAppswitchComponent,
    data: { standalone: true }
  },
  {
    path: 'signedout',
    component: SignedOutComponent
  },
  {
    path: 'app',
    loadChildren: () => import('./common-features/common-features.module').then((m) => m.CommonFeaturesModule),
    canActivate: [RedirectEidGuard]
  },
  {
    path: 'program-setup',
    loadChildren: () => import('./program-setup/program-setup.module').then((m) => m.ProgramSetupModule),
    canActivateChild: [AuthGuard, RoleGuard, TargetSiteProgramGuard],
    data: {
      roles: {
        program: [ProgramRole.Coordinator],
        site: [SiteRole.SiteAdministrator]
      }
    }
  },
  {
    path: 'field-training',
    loadChildren: () => import('./field-training/field-training.module').then((m) => m.FieldTrainingModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard],
    data: { programType: ProgramType.FieldTraining }
  },
  {
    path: 'performance-evaluation',
    loadChildren: () =>
      import('./performance-evaluation/performance-evaluation.module').then((m) => m.PerformanceEvaluationModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard],
    data: { programType: ProgramType.PerformanceEvaluation }
  },
  {
    path: 'mentoring',
    loadChildren: () => import('./mentoring/mentoring.module').then((m) => m.MentoringModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard],
    data: { programType: ProgramType.Mentoring }
  },
  {
    path: 'probationary-period',
    loadChildren: () =>
      import('./probationary-period/probationary-period.module').then((m) => m.ProbationaryPeriodModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard],
    data: { programType: ProgramType.ProbationaryPeriod }
  },
  {
    path: 'reports',
    loadChildren: () => import('./reports/reports.module').then((m) => m.ReportsModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard, ResponsiveGuard],
    data: { programType: ProgramType.FieldTraining }
  },
  {
    path: 'mt-reports',
    loadChildren: () => import('./mt-reports/mt-reports.module').then((m) => m.MtReportsModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard, ResponsiveGuard],
    data: { programType: ProgramType.Mentoring }
  },
  {
    path: 'pe-reports',
    loadChildren: () => import('./pe-reports/pe-reports.module').then((m) => m.PeReportsModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard, ResponsiveGuard],
    data: { programType: ProgramType.PerformanceEvaluation }
  },
  {
    path: 'pp-reports',
    loadChildren: () => import('./pp-reports/pp-reports.module').then((m) => m.PpReportsModule),
    canActivateChild: [AuthGuard, TargetSiteProgramGuard, ResponsiveGuard],
    data: { programType: ProgramType.ProbationaryPeriod }
  },
  {
    path: 'site-administration',
    loadChildren: () =>
      import('./site-administration/site-administration.module').then((m) => m.SiteAdministrationModule),
    canActivateChild: [AuthGuard, RoleGuard, TargetSiteProgramGuard],
    data: {
      roles: {
        site: [SiteRole.SiteAdministrator]
      },
      isSiteLevel: true
    }
  },
  {
    path: 'system-administration',
    loadChildren: () =>
      import('./system-administration/system-administration.module').then((m) => m.SystemAdministrationModule),
    canActivateChild: [AuthGuard]
  },
  {
    path: 'training-courses',
    loadChildren: () => import('./training-courses/training-courses.module').then((m) => m.TrainingCoursesModule),
    canActivateChild: [AuthGuard, EnableOnSiteGuard, ResponsiveGuard, TargetSiteProgramGuard],
    data: { featureFlag: 'TrainingCoursesEnabled' }
  },
  {
    path: '**',
    pathMatch: 'full',
    component: BlankComponent,
    canActivate: [RedirectGuard]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(rootRoutes, { useHash: false, paramsInheritanceStrategy: 'emptyOnly' })],
  exports: [RouterModule]
})
export class RootRoutingModule {}
